import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  ExpandableSection,
  Form,
  FormField,
  Header,
  Input,
  SegmentedControl,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react";
import type { RowSeed } from "./investigationUtils";

export type RowFormSubmission =
  | { kind: "pk"; seed: RowSeed }
  | { kind: "txn"; transactionId: string };

type LookupMode = "txn" | "pk";

interface InvestigateRowFormProps {
  onSubmit: (submission: RowFormSubmission) => void;
  /** Omit when the form is the drawer's home view (nothing to cancel back to). */
  onCancel?: () => void;
}

const MODE_COPY: Record<
  LookupMode,
  {
    label: string;
    description: string;
    placeholder: string;
    missing: string;
    action: string;
  }
> = {
  txn: {
    label: "Transaction / order ID",
    description:
      "Shows every DLQ item for this transaction in the latest snapshot, grouped by error, so you can pick which group to investigate.",
    placeholder: "e.g. D01-1234567-1234567 or an AUID",
    missing: "Enter a transaction or order ID",
    action: "Find DLQ items",
  },
  pk: {
    label: "DLQ PK",
    description:
      "The item's primary key, copied from a dashboard row. Its queue and error are read from the snapshot automatically.",
    placeholder: "Paste the dlq_pk value from the dashboard",
    missing: "Enter a DLQ PK",
    action: "Look up item",
  },
};

/**
 * Single-item entry point for the investigations flow. One identifier at a time: humans usually
 * hold a transaction/order ID (DLQ PKs are opaque), so that is the default. The snapshot fields
 * that seed a PK's population rule are filled by the live lookup, so they are tucked away as an
 * override for items newer than the snapshot.
 */
export const InvestigateRowForm: React.FC<InvestigateRowFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const [mode, setMode] = useState<LookupMode>("txn");
  const [identifier, setIdentifier] = useState("");
  const [eventsourcekey, setEventsourcekey] = useState("");
  const [errorType, setErrorType] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState<string | undefined>();
  const copy = MODE_COPY[mode];

  const switchMode = (next: LookupMode) => {
    setMode(next);
    setError(undefined);
  };

  const submit = () => {
    const value = identifier.trim();
    if (!value) {
      setError(copy.missing);
      return;
    }
    setError(undefined);
    if (mode === "txn") {
      onSubmit({ kind: "txn", transactionId: value });
      return;
    }
    onSubmit({
      kind: "pk",
      seed: {
        dlqPk: value,
        eventsourcekey: eventsourcekey.trim() || undefined,
        error_type: errorType.trim() || undefined,
        error_message: errorMessage.trim() || undefined,
      },
    });
  };

  return (
    <Container
      header={
        <Header
          variant="h3"
          description="Paste an identifier to find its DLQ items and see whether anyone is already investigating them."
        >
          Investigate a DLQ item
        </Header>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {onCancel && (
                <Button variant="link" formAction="none" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button variant="primary" formAction="submit">
                {copy.action}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            <FormField label="Look up by">
              <SegmentedControl
                selectedId={mode}
                onChange={({ detail }) =>
                  switchMode(detail.selectedId as LookupMode)
                }
                label="Look up by"
                options={[
                  { id: "txn", text: "Transaction / order ID" },
                  { id: "pk", text: "DLQ PK" },
                ]}
              />
            </FormField>
            <FormField
              label={copy.label}
              description={copy.description}
              errorText={error}
              stretch
            >
              <Input
                key={mode}
                value={identifier}
                placeholder={copy.placeholder}
                onChange={({ detail }) => {
                  setIdentifier(detail.value);
                  if (error) setError(undefined);
                }}
                autoFocus
              />
            </FormField>
            {mode === "pk" && (
              <ExpandableSection
                variant="footer"
                headerText="Override snapshot fields"
                headerDescription="Only needed when the item is newer than the latest hourly snapshot and its queue or error cannot be read automatically."
              >
                <SpaceBetween size="s">
                  <FormField label="Queue / event source key">
                    <Input
                      value={eventsourcekey}
                      onChange={({ detail }) => setEventsourcekey(detail.value)}
                    />
                  </FormField>
                  <FormField label="Error type">
                    <Input
                      value={errorType}
                      onChange={({ detail }) => setErrorType(detail.value)}
                    />
                  </FormField>
                  <FormField
                    label="Templated error message"
                    description="Use <*> for the parts that vary between items."
                  >
                    <Textarea
                      value={errorMessage}
                      onChange={({ detail }) => setErrorMessage(detail.value)}
                      rows={3}
                    />
                  </FormField>
                </SpaceBetween>
              </ExpandableSection>
            )}
            {mode === "txn" && (
              <Box color="text-body-secondary" fontSize="body-s">
                Have a DLQ PK from a dashboard row instead? Switch to{" "}
                <strong>DLQ PK</strong> above.
              </Box>
            )}
          </SpaceBetween>
        </Form>
      </form>
    </Container>
  );
};

export default InvestigateRowForm;
